import type { Context } from '@/models/report';

/** The stored number behind a context entry: the full-precision measurement
 *  value where there is one, else the displayed text. Converting from the
 *  displayed text would round twice (54.43 kg shows "54", which is 119 lbs,
 *  not the 120 the reader typed). */
function numberOf(ctx?: Context): number | undefined {
  if (!ctx?.value) return undefined;
  const value = ctx.numericValue ?? Number(ctx.value);
  return value === null || Number.isNaN(value) ? undefined : value;
}

/** Whole feet and remaining inches for a metric height, display only. */
export function imperialHeightParts(
  ctx?: Context
): { feet: string; inches: string } | undefined {
  const value = numberOf(ctx);
  if (value === undefined) return undefined;
  const inches = Math.round(value / 2.54);
  return { feet: String(Math.floor(inches / 12)), inches: String(inches % 12) };
}

/** Feet and inches typed by the reader → cm. Both blank clears; a
 *  non-numeric part is ignored (undefined). */
export function heightPartsToCm(
  feet: string,
  inches: string
): number | null | undefined {
  if (feet.trim() === '' && inches.trim() === '') return null;
  const ft = feet.trim() === '' ? 0 : Number(feet);
  const inch = inches.trim() === '' ? 0 : Number(inches);
  if (Number.isNaN(ft) || Number.isNaN(inch)) return undefined;
  return (ft * 12 + inch) * 2.54;
}

export function lbsToKg(lbs: number): number {
  return lbs / 2.20462;
}

/** Display only — the stored context values stay metric. */
export function toImperial(ctx?: Context): Context | undefined {
  const value = numberOf(ctx);
  if (!ctx || value === undefined) return ctx;

  if (ctx.contextClass === 'HEIGHT') {
    const inches = Math.round(value / 2.54);
    return {
      ...ctx,
      value: `${Math.floor(inches / 12)}'${inches % 12}"`,
      unit: 'ft/in',
    };
  }
  if (ctx.contextClass === 'WEIGHT') {
    return { ...ctx, value: String(Math.round(value * 2.20462)), unit: 'lbs' };
  }
  return ctx;
}
